import { c as _c } from "react/compiler-runtime";
import { IcoCheck16, IcoCloseSmall16, IcoPaint16 } from '@onefootprint/icons';
import { IconButton, Popover } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type ChangeEvent, type RefObject, useState } from 'react';
import { type RgbColor, RgbColorPicker } from 'react-colorful';
import { colorsMatch, colorToRgba, rgbaToHex } from './utils';

// Unique class name for styling react-colorful picker (global CSS required for third-party component)
const COLOR_PICKER_CLASS = 'fp-rgba-picker-k9x2m7v4p1';
const predefinedColors = ['#000000',
// Black
'#6B7280',
// Gray
'#EF4444',
// Red
'#F97316',
// Orange
'#EAB308',
// Yellow
'#22C55E',
// Green
'#3B82F6',
// Blue
'#8B5CF6',
// Purple
'#EC4899' // Pink
];
type ColorComponentProps = {
  value?: string;
  onChange?: (color: string) => void;
  portalContainerRef?: RefObject<HTMLElement | null>;
  defaultOpen?: boolean;
};
const ColorComponent = ({
  value = '#000000',
  onChange,
  portalContainerRef,
  defaultOpen = false
}: ColorComponentProps) => {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(defaultOpen);
  const [isAdvancedPickerOpen, setIsAdvancedPickerOpen] = useState(false);
  const portalContainer = portalContainerRef?.current ?? null;
  const handleColorChange = (newColor: string) => {
    onChange?.(newColor);
  };
  return <Popover side="bottom" controlledOpen={isColorPickerOpen} onControlledOpenChange={setIsColorPickerOpen} onPointerDownOutside={e => {
    if (isAdvancedPickerOpen) {
      e.preventDefault();
    }
  }} portalContainer={portalContainer} content={<div>
          <ColorPickerContent color={value} onColorChange={handleColorChange} isAdvancedPickerOpen={isAdvancedPickerOpen} onAdvancedPickerOpenChange={setIsAdvancedPickerOpen} portalContainer={portalContainer} />
        </div>}>
      <button type="button" className="flex items-center justify-center w-[32px] h-[32px] shrink-0 rounded-default border border-primary cursor-pointer" onClick={() => setIsColorPickerOpen(true)}>
        <div className="w-[12px] h-[12px] rounded-full" style={{
        backgroundColor: value
      }} />
      </button>
    </Popover>;
};
type ColorPickerContentProps = {
  color: string;
  onColorChange: (color: string) => void;
  isAdvancedPickerOpen: boolean;
  onAdvancedPickerOpenChange: (open: boolean) => void;
  portalContainer?: HTMLElement | null;
};
const ColorPickerContent = t0 => {
  const $ = _c(75);
  const {
    color,
    onColorChange,
    isAdvancedPickerOpen,
    onAdvancedPickerOpenChange,
    portalContainer
  } = t0;
  let t1;
  if ($[0] !== color) {
    t1 = () => {
      const rgba = colorToRgba(color);
      return {
        r: rgba.r,
        g: rgba.g,
        b: rgba.b
      };
    };
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [tempRgb, setTempRgb] = useState(t1);
  let t2;
  if ($[2] !== color) {
    t2 = rgbaToHex(colorToRgba(color)).toUpperCase();
    $[2] = color;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [hexInputValue, setHexInputValue] = useState(t2);
  let t3;
  if ($[4] !== color) {
    t3 = predefinedColors.findIndex(preset => colorsMatch(color, preset));
    $[4] = color;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const matchedPresetIndex = t3;
  const isCustomColor = matchedPresetIndex === -1;
  const showCustomSlot = isCustomColor || isAdvancedPickerOpen;
  let t4;
  if ($[6] !== onColorChange) {
    t4 = presetColor => {
      onColorChange(presetColor);
    };
    $[6] = onColorChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handlePresetClick = t4;
  let t5;
  if ($[8] !== color || $[9] !== onAdvancedPickerOpenChange) {
    t5 = () => {
      const rgba_0 = colorToRgba(color);
      setTempRgb({
        r: rgba_0.r,
        g: rgba_0.g,
        b: rgba_0.b
      });
      setHexInputValue(rgbaToHex(rgba_0).toUpperCase());
      onAdvancedPickerOpenChange(true);
    };
    $[8] = color;
    $[9] = onAdvancedPickerOpenChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleAdvancedPickerOpen = t5;
  let t6;
  if ($[11] !== onAdvancedPickerOpenChange || $[12] !== onColorChange || $[13] !== tempRgb) {
    t6 = () => {
      const newColor = rgbaToHex({
        ...tempRgb,
        a: 1
      }).toUpperCase();
      onColorChange(newColor);
      onAdvancedPickerOpenChange(false);
    };
    $[11] = onAdvancedPickerOpenChange;
    $[12] = onColorChange;
    $[13] = tempRgb;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleAdvancedPickerSave = t6;
  let t7;
  if ($[15] !== color || $[16] !== onAdvancedPickerOpenChange) {
    t7 = () => {
      const rgba_1 = colorToRgba(color);
      setTempRgb({
        r: rgba_1.r,
        g: rgba_1.g,
        b: rgba_1.b
      });
      setHexInputValue(rgbaToHex(rgba_1).toUpperCase());
      onAdvancedPickerOpenChange(false);
    };
    $[15] = color;
    $[16] = onAdvancedPickerOpenChange;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleAdvancedPickerClose = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => {
      const inputValue = e.target.value.toUpperCase();
      setHexInputValue(inputValue);
      const hex = inputValue.startsWith("#") ? inputValue : `#${inputValue}`;
      if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
        const parsed = colorToRgba(hex);
        setTempRgb({
          r: parsed.r,
          g: parsed.g,
          b: parsed.b
        });
      }
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleHexInputChange = t8;
  let t9;
  if ($[19] !== color || $[20] !== isAdvancedPickerOpen || $[21] !== tempRgb) {
    t9 = isAdvancedPickerOpen ? rgbaToHex({
      ...tempRgb,
      a: 1
    }) : color;
    $[19] = color;
    $[20] = isAdvancedPickerOpen;
    $[21] = tempRgb;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const customSlotColor = t9;
  const isCustomSlotSelected = !isAdvancedPickerOpen && isCustomColor;
  let t10;
  if ($[23] !== handlePresetClick || $[24] !== isAdvancedPickerOpen || $[25] !== matchedPresetIndex) {
    t10 = predefinedColors.map((presetColor_0, index) => {
      const isSelected = !isAdvancedPickerOpen && matchedPresetIndex === index;
      return <button key={presetColor_0} type="button" className={cx("flex items-center justify-center w-[32px] h-[32px] rounded-full shrink-0 cursor-pointer transition-all duration-200", isSelected && "border-2 border-accent")} onClick={() => handlePresetClick(presetColor_0)}><div className="w-[24px] h-[24px] rounded-full flex items-center justify-center" style={{
          backgroundColor: presetColor_0
        }}>{isSelected && <IcoCheck16 color="quinary" />}</div></button>;
    });
    $[23] = handlePresetClick;
    $[24] = isAdvancedPickerOpen;
    $[25] = matchedPresetIndex;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = showCustomSlot ? "w-[32px] opacity-100" : "w-0 opacity-0";
  let t12;
  if ($[27] !== t11) {
    t12 = cx("transition-all duration-200 ease-out overflow-hidden", t11);
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cx("flex items-center justify-center w-[32px] h-[32px] rounded-full shrink-0 cursor-pointer transition-all duration-200", "border-2 border-accent");
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== customSlotColor) {
    t14 = {
      backgroundColor: customSlotColor
    };
    $[30] = customSlotColor;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== isCustomSlotSelected) {
    t15 = isCustomSlotSelected && <IcoCheck16 color="quinary" />;
    $[32] = isCustomSlotSelected;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = <button type="button" className={t13}><div className="w-[24px] h-[24px] rounded-full flex items-center justify-center transition-colors duration-200" style={t14}>{t15}</div></button>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t12 || $[38] !== t16) {
    t17 = <div className={t12}>{t16}</div>;
    $[37] = t12;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t10 || $[41] !== t17) {
    t18 = <div className="flex flex-wrap items-center gap-3 md:flex-nowrap">{t10}{t17}</div>;
    $[40] = t10;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="w-[1px] h-[24px] border-l border-tertiary" />;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <style jsx={true} global={true}>{`
                .${COLOR_PICKER_CLASS} {
                  width: 240px !important;
                  height: auto !important;
                  gap: 16px !important;
                }
                .${COLOR_PICKER_CLASS} .react-colorful__saturation {
                  height: 184px !important;
                  border-radius: 4px !important;
                }
                .${COLOR_PICKER_CLASS} .react-colorful__hue {
                  height: 8px !important;
                  border-radius: 100px !important;
                }
                .${COLOR_PICKER_CLASS} .react-colorful__saturation-pointer,
                .${COLOR_PICKER_CLASS} .react-colorful__hue-pointer,
                .${COLOR_PICKER_CLASS} .react-colorful__pointer {
                  width: 8px !important;
                  height: 8px !important;
                  border: 2px solid #ffffff !important;
                  box-shadow: 0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(31, 41, 55, 0.1) !important;
                }
              `}</style>;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== handleAdvancedPickerClose) {
    t21 = <IconButton onClick={handleAdvancedPickerClose} aria-label="Close color picker" className="max-md:-ml-3 max-md:size-11" size="tiny" icon={IcoCloseSmall16} color="tertiary" />;
    $[45] = handleAdvancedPickerClose;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="text-label-2 text-primary">Color</span>;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== handleAdvancedPickerSave) {
    t23 = <button type="button" onClick={handleAdvancedPickerSave} className="-mr-3 h-11 px-3 text-label-2 text-accent cursor-pointer md:mr-0 md:h-auto md:px-0">Save</button>;
    $[48] = handleAdvancedPickerSave;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t21 || $[51] !== t23) {
    t24 = <div className="flex items-center justify-between h-[44px] px-3 border-b border-tertiary">{t21}{t22}{t23}</div>;
    $[50] = t21;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = newColor_0 => {
      setTempRgb(newColor_0);
      setHexInputValue(rgbaToHex({
        ...newColor_0,
        a: 1
      }).toUpperCase());
    };
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== tempRgb) {
    t26 = <div className="flex flex-col gap-4"><RgbColorPicker color={tempRgb} onChange={t25} className={COLOR_PICKER_CLASS} /></div>;
    $[54] = tempRgb;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== hexInputValue) {
    t27 = <div><input type="text" value={hexInputValue} onChange={handleHexInputChange} className="w-full h-8 px-4 text-body-2 text-primary bg-primary border border-solid border-primary rounded-default focus:outline-none focus:border-accent" /></div>;
    $[56] = hexInputValue;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t26 || $[59] !== t27) {
    t28 = <div className="p-4 flex flex-col gap-5">{t26}{t27}</div>;
    $[58] = t26;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t24 || $[62] !== t28) {
    t29 = <div>{t20}<div className="w-[272px] flex flex-col bg-primary rounded-default overflow-hidden">{t24}{t28}</div></div>;
    $[61] = t24;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== handleAdvancedPickerOpen) {
    t30 = <IconButton aria-label="Open color picker" icon={IcoPaint16} rounded={true} onClick={handleAdvancedPickerOpen} />;
    $[64] = handleAdvancedPickerOpen;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== isAdvancedPickerOpen || $[67] !== onAdvancedPickerOpenChange || $[68] !== portalContainer || $[69] !== t29 || $[70] !== t30) {
    t31 = <div className="flex items-center justify-center gap-2">{t19}<Popover side="bottom" align="end" portalContainer={portalContainer} controlledOpen={isAdvancedPickerOpen} onControlledOpenChange={onAdvancedPickerOpenChange} onPointerDownOutside={_temp} content={t29}>{t30}</Popover></div>;
    $[66] = isAdvancedPickerOpen;
    $[67] = onAdvancedPickerOpenChange;
    $[68] = portalContainer;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t18 || $[73] !== t31) {
    t32 = <div className="flex max-w-[calc(100vw-32px)] items-center gap-4 p-3 md:max-w-none">{t18}{t31}</div>;
    $[72] = t18;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  return t32;
};
export default ColorComponent;
function _temp(e_0) {
  return e_0.preventDefault();
}